import { useCallback, useContext } from 'react';

import {
  RISK_METRIC_LIST,
  excludeDisabledRiskMetrics,
} from '../../constants/reports/reports-filters';
import { IAccountOption, PortfolioManagementContext } from '../portfolio-management';
import { WebSocketContext } from '../websocket-context/websocket-provider';

import { RiskFiltersContext } from './risk-filters-provider';
import { IRiskReportDataLoadedParams } from './types';

const LIST_LOAD_RISK_REPORT_ID = 'LIST_LOAD_RISK_REPORT';

interface ILoadRiskReport {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useLoadRiskReport = ({ setLoading }: ILoadRiskReport) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const { addSpotDataToAccount, riskSettingsSelected } = useContext(PortfolioManagementContext);
  const {
    filters: { riskToken },
  } = useContext(RiskFiltersContext);

  const handleRiskReportDataLoadedResponse = useCallback(
    (response: any, accounts: IAccountOption, timestamp?: string) => {
      if (!response) {
        setLoading(false);
        return;
      }

      const riskMetricPositions = RISK_METRIC_LIST.reduce((acc: any, metric) => {
        acc[metric.value] = metric.position;
        return acc;
      }, {});

      // A disabled metric must never reach the backend: an undefined metric
      // fails the whole risk run. Scrub first, then sort, so the rendered set
      // matches what is requested (same pattern as risk-management-provider).
      const requestableRiskMetrics = excludeDisabledRiskMetrics(riskSettingsSelected.riskMetrics);

      const sortedRiskMetrics = [...requestableRiskMetrics].sort(
        (a, b) => (riskMetricPositions[a] || 0) - (riskMetricPositions[b] || 0),
      );

      const { result, error } = response;

      const data = response;
      const errorFlag = !result && error;

      addSpotDataToAccount({
        account: accounts,
        dataParent: 'SPOT_LADDER',
        data,
        error: errorFlag,
        riskToken,
        timestamp,
        riskMetrics: sortedRiskMetrics,
        isSpotOverride: riskSettingsSelected.isSpotOverride,
        isFreezeSpotOverride: riskSettingsSelected.isFreezeSpotOverride,
        freezeSpotValue: riskSettingsSelected.freezeSpotValue,
        isOtcTraderVols: riskSettingsSelected.isOtcTraderVols,
      });

      setLoading(false);
    },
    [riskSettingsSelected, riskToken],
  );

  const getLoadRiskReport = ({ accounts, timestamp }: IRiskReportDataLoadedParams) => {
    const source =
      accounts.exchange === 'otc'
        ? { account: accounts.name, venue: accounts.venue }
        : { name: accounts.name };

    const payload = {
      jsonrpc: '2.0',
      id: LIST_LOAD_RISK_REPORT_ID,
      method: 'load_risk_report',
      params: {
        timestamp,
        source,
      },
    };

    setLoading(true);
    sendMessage(JSON.stringify(payload));
    subscribeToChannel(LIST_LOAD_RISK_REPORT_ID, (response: any) =>
      handleRiskReportDataLoadedResponse(response, accounts, timestamp),
    );
  };

  return {
    getLoadRiskReport,
  };
};
